import { useEffect, useState } from "react";
import { Trash2 } from "lucide-react";
import { InfoBanner } from "@/components/ui/banner";
import { Button } from "@/components/ui/button";
import { SupportDetails } from "@/components/ui/copyable-reference";
import { Field, Input } from "@/components/ui/input";
import { Modal } from "@/components/ui/modal";
import { Select } from "@/components/ui/select";
import { formatTemplate, type SsoLocaleCatalog } from "@/i18n/use-sso-locale";
import { refusalMessage } from "@/features/admin/sso/sso-shared";
import {
  updateSsoProviderRoles,
  withStepUpRetry,
  type ApiSsoProvider,
  type ApiSsoRoleMapping,
} from "@/lib/api";

/**
 * Admin → Single sign-on → role mapping dialog (ALL-5464, page-spec action
 * `admin_maps_groups_to_non_privileged_roles`).
 *
 * Calls exactly one operation: `updateProviderRoles`
 * (PUT /v1/admin/sso/providers/{id}/roles). The mode is either
 * `EXPLICIT_ONLY` (Alloy-managed roles, which the contract requires to be sent
 * with an empty `mappings` array) or `MAP_GROUPS` (each IdP group maps to one
 * Alloy role).
 *
 * The Alloy role field is free text on purpose. Which roles are privileged —
 * the ones carrying admin, approval, key or invitation scopes — is the
 * server's rule, and it refuses them with 422 SSO_ROLE_PRIVILEGED; this page
 * deliberately does not re-derive that classification, because a client-side
 * copy of a security rule is a second place for it to be wrong. What it does
 * instead is state the rule up front and render the server's refusal, with its
 * support reference, inline. The API stays the enforcement point.
 */
export function SsoRoleMappingDialog({
  provider,
  onOpenChange,
  catalog,
  onSaved,
}: {
  provider: ApiSsoProvider | null;
  onOpenChange: (open: boolean) => void;
  catalog: SsoLocaleCatalog;
  onSaved: () => void;
}) {
  const ui = catalog.ui;
  const [mode, setMode] = useState<"EXPLICIT_ONLY" | "MAP_GROUPS">("EXPLICIT_ONLY");
  const [rows, setRows] = useState<ApiSsoRoleMapping[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<{ message: string; supportReference?: string; capability?: string } | null>(null);
  const [saved, setSaved] = useState(false);
  const [busy, setBusy] = useState(false);

  // The dialog opens on whatever the provider actually has, so an operator
  // never edits a mode the API did not report. The mapping rows start empty on
  // purpose: the PUT is a full-set replace, and seeding the editor with the
  // server's current rows puts a second, editable copy of the mapping set on
  // screen — the operator's first `Add mapping` then saves both copies, so the
  // dialog would silently double the provider's mappings. The rows the operator
  // adds here ARE the set that gets saved.
  useEffect(() => {
    if (!provider) return;
    const nextMode = provider.roleMappingMode === "MAP_GROUPS" ? "MAP_GROUPS" : "EXPLICIT_ONLY";
    setMode(nextMode);
    setRows([]);
    setError(null);
    setNotice(null);
    setSaved(false);
  }, [provider]);

  function updateRow(index: number, patch: Partial<ApiSsoRoleMapping>) {
    setRows((current) => current.map((row, i) => (i === index ? { ...row, ...patch } : row)));
  }

  async function handleSave() {
    if (!provider) return;
    const mappings = mode === "MAP_GROUPS"
      ? rows.map((row) => ({ group: row.group.trim(), roleName: row.roleName.trim() }))
      : [];
    if (mappings.some((row) => !row.group || !row.roleName)) {
      setError(ui["sso.roles.incomplete"]);
      return;
    }
    setError(null);
    setNotice(null);
    setSaved(false);
    setBusy(true);
    const outcome = await withStepUpRetry(() => updateSsoProviderRoles(provider.identityProviderId, mode, mappings));
    setBusy(false);

    if (outcome.stepUpFailed) {
      setNotice({
        message: ui["sso.shared.stepUpDescription"],
        supportReference: outcome.stepUpSupportReference,
      });
      return;
    }
    const result = outcome.result;
    if (!result.ok) {
      setNotice({
        message: refusalMessage(ui, result.reasonCode) ?? ui["sso.roles.incomplete"],
        supportReference: result.supportReference,
        capability: result.capability,
      });
      return;
    }
    setSaved(true);
    onSaved();
  }

  return (
    <Modal
      open={provider !== null}
      onOpenChange={(next) => {
        if (!busy) onOpenChange(next);
      }}
      title={ui["sso.roles.title"]}
      description={ui["sso.roles.description"]}
      size="md"
      footer={
        <>
          <Button variant="secondary" onClick={() => onOpenChange(false)} disabled={busy}>
            {ui["sso.shared.cancel"]}
          </Button>
          <Button loading={busy} onClick={() => void handleSave()}>
            {busy ? ui["sso.roles.saving"] : ui["sso.roles.save"]}
          </Button>
        </>
      }
    >
      {notice && (
        <div data-testid="sso-roles-notice">
          <InfoBanner variant="danger">
            {notice.message}
            <SupportDetails reference={notice.supportReference} capability={notice.capability} />
          </InfoBanner>
        </div>
      )}
      {saved && (
        <div data-testid="sso-roles-saved">
          <InfoBanner variant="success">{ui["sso.roles.saved"]}</InfoBanner>
        </div>
      )}

      <div className="mt-1 flex flex-col gap-4">
        <div className="rounded-md border border-line bg-bg px-3 py-2.5">
          <p className="text-xs leading-relaxed text-muted">{ui["sso.roles.privilegedWarning"]}</p>
        </div>

        <Field label={ui["sso.roles.mode"]} htmlFor="sso-roles-mode" hint={ui["sso.roles.modeHint"]}>
          <Select
            id="sso-roles-mode"
            name="mode"
            className="w-full"
            value={mode}
            onChange={(event) => setMode(event.target.value as "EXPLICIT_ONLY" | "MAP_GROUPS")}
            disabled={busy}
          >
            <option value="EXPLICIT_ONLY">{ui["sso.providers.roleMode.EXPLICIT_ONLY"]}</option>
            <option value="MAP_GROUPS">{ui["sso.providers.roleMode.MAP_GROUPS"]}</option>
          </Select>
        </Field>

        {mode === "MAP_GROUPS" ? (
          <div className="flex flex-col gap-3">
            {rows.map((row, index) => (
              // Two `Field`s (each a full-width control) plus a remove button do
              // not fit on one line inside a 375px dialog: the two inputs' own
              // min-content width alone is ~360px. Below `sm` the row stacks —
              // group, role, then the remove button — and from `sm` it is the
              // designer's single line again. `min-w-0 flex-1` is what lets each
              // input shrink inside its column instead of forcing the dialog
              // wider than the viewport.
              <div
                key={index}
                data-testid={`sso-role-mapping-row-${index}`}
                className="flex flex-col gap-2 sm:flex-row sm:items-end"
              >
                <div className="min-w-0 flex-1">
                  <Field label={ui["sso.roles.group"]} htmlFor={`sso-role-group-${index}`}>
                    <Input
                      id={`sso-role-group-${index}`}
                      value={row.group}
                      onChange={(event) => updateRow(index, { group: event.target.value })}
                      placeholder={ui["sso.roles.groupPlaceholder"]}
                      autoComplete="off"
                      spellCheck={false}
                      disabled={busy}
                    />
                  </Field>
                </div>
                <div className="min-w-0 flex-1">
                  <Field label={ui["sso.roles.role"]} htmlFor={`sso-role-role-${index}`}>
                    <Input
                      id={`sso-role-role-${index}`}
                      value={row.roleName}
                      onChange={(event) => updateRow(index, { roleName: event.target.value })}
                      placeholder={ui["sso.roles.rolePlaceholder"]}
                      autoComplete="off"
                      spellCheck={false}
                      disabled={busy}
                    />
                  </Field>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  iconOnly
                  // 36px square (the lane's tap-target floor) instead of the
                  // `sm` icon button's 32px, and `self-start` so it sits on its
                  // own line's start when the row is stacked.
                  className="size-9 self-start sm:self-auto"
                  aria-label={formatTemplate(ui["sso.roles.removeRow"], { index: String(index + 1) })}
                  disabled={busy}
                  onClick={() => setRows((current) => current.filter((_, i) => i !== index))}
                >
                  <Trash2 aria-hidden className="size-3.5" />
                </Button>
              </div>
            ))}
            <div>
              <Button
                variant="secondary"
                size="sm"
                disabled={busy}
                onClick={() => setRows((current) => [...current, { group: "", roleName: "" }])}
              >
                {ui["sso.roles.addRow"]}
              </Button>
            </div>
          </div>
        ) : (
          <p className="rounded-md border border-line bg-bg px-3 py-2 text-xs text-muted">
            {ui["sso.roles.noneNeeded"]}
          </p>
        )}

        {error && <p className="text-xs text-danger">{error}</p>}
      </div>
    </Modal>
  );
}
